/**
 * Exact minor-unit money display formatter (lease
 * way-display-money-20261012-01). Preserves the incumbent en-CA currency
 * presentation — `$` for CAD, `US$` for USD, sign before the symbol,
 * three-digit comma grouping and exactly two decimals — while splitting
 * whole dollars and cents with BigInt so every safe-integer minor-unit
 * amount renders its exact cents instead of through Number/100 floating
 * rounding. Pure: no locale conversion, currency conversion, rate
 * inference, record, Store, Domain, provider or caller state involvement.
 */
import type { Currency } from './contracts';

const NOT_RECORDED = 'Not recorded';
const AMOUNT_UNAVAILABLE = 'Amount unavailable';
const CURRENCY_UNAVAILABLE = 'Currency unavailable';
const CURRENCY_SYMBOLS: Readonly<Record<Currency, string>> = { CAD: '$', USD: 'US$' };
const WHOLE_GROUPING = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 });

export function formatMinorMoney(
  value: number | null | undefined,
  currency: Currency = 'CAD',
): string {
  if (value === null || value === undefined) return NOT_RECORDED;
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) return AMOUNT_UNAVAILABLE;
  if (currency !== 'CAD' && currency !== 'USD') return CURRENCY_UNAVAILABLE;
  const symbol = CURRENCY_SYMBOLS[currency];
  const negative = value < 0;
  const absoluteMinor = BigInt(negative ? -value : value);
  const cents = absoluteMinor % 100n;
  const groupedWhole = WHOLE_GROUPING.format(Number(absoluteMinor / 100n));
  return `${negative ? '-' : ''}${symbol}${groupedWhole}.${cents.toString().padStart(2, '0')}`;
}
